import type { ReactNode } from "react";
import styled from "styled-components";
import { i18nFilter } from "app/filters/i18nFilter";
import { PopupHoverContainer } from "@similarweb/ui-components";
import { Label } from "../StyledComponents/StyledLabel";
import { setFontVariant } from "@similarweb/styles";
import type { Intent } from "app/pages/website-analysis/traffic-sources/search/common/intent.configuration";
import {
    INTENT_PAYLOAD_TO_TOOLTIP_INFORMATION_FULL_TEXT,
    MAP_INTENT_PAYLOAD_TO_INTENT_REPRESENTATION,
    MAP_INTENT_PAYLOAD_TO_INTENT_TOOLTIP_DISCLAIMER,
} from "app/pages/website-analysis/traffic-sources/search/common/intent.configuration";
import { Flex, Stack } from "@similarweb/ui-components/foundations";
import { colorsPalettes } from "@similarweb/styles";

export const NO_DATA_MESSAGE = "-";

const filter = i18nFilter();

export const IntentHeaderWrapper = styled(Flex)`
    justify-content: flex-start;
    gap: 4px;
    align-items: center;
`;

const IntentWrapper = styled(Stack)<{ marginBottom?: string | number }>`
    margin-bottom: ${({ marginBottom }) => (marginBottom ? marginBottom : 0)};
`;

const TooltipTitle = styled.div`
    ${setFontVariant("subtitle1", colorsPalettes.carbon[400])};
    margin-bottom: 4px;
`;

export const TooltipSubTitle = styled.div`
    ${setFontVariant("listItem3")};
`;

const TooltipInformationalFullTextWrapper = styled.div`
    ${setFontVariant("subtitle2", colorsPalettes.carbon[400])};
    box-sizing: border-box;
    border-radius: 8px;
    padding: 4px 6px;
`;

const DisclaimerTextWrapper = styled.div`
    ${setFontVariant("body2", colorsPalettes.carbon[400])};
`;

const TooltipWrapper = styled(Stack)`
    width: 292px;
`;

const IntentCellTooltip = ({
    children,
    searchTerm,
    date,
    PrimaryIntent,
    SecondaryIntent,
}: {
    children: ReactNode;
    searchTerm: string;
    date: string;
    PrimaryIntent: string;
    SecondaryIntent: string;
}) => {
    const marginBetweenIntents = SecondaryIntent ? "24px" : 0;
    return (
        <PopupHoverContainer
            content={() => (
                <TooltipWrapper>
                    <TooltipTitle>
                        {filter("keywords.table.filters.intent.cell.tooltip.title", { searchTerm })}
                    </TooltipTitle>
                    <TooltipSubTitle>
                        {filter("keywords.table.filters.intent.cell.tooltip.subtitle", { date })}
                    </TooltipSubTitle>
                    <IntentWrapper marginBottom={marginBetweenIntents}>
                        <IntentHeaderWrapper>
                            <Label>{MAP_INTENT_PAYLOAD_TO_INTENT_REPRESENTATION[PrimaryIntent]}</Label>
                            <TooltipInformationalFullTextWrapper>
                                {INTENT_PAYLOAD_TO_TOOLTIP_INFORMATION_FULL_TEXT[PrimaryIntent]}
                            </TooltipInformationalFullTextWrapper>
                        </IntentHeaderWrapper>
                        <DisclaimerTextWrapper>
                            {MAP_INTENT_PAYLOAD_TO_INTENT_TOOLTIP_DISCLAIMER[PrimaryIntent]}
                        </DisclaimerTextWrapper>
                    </IntentWrapper>
                    {SecondaryIntent ? (
                        <IntentWrapper>
                            <IntentHeaderWrapper>
                                <Label>{MAP_INTENT_PAYLOAD_TO_INTENT_REPRESENTATION[SecondaryIntent]}</Label>
                                <TooltipInformationalFullTextWrapper>
                                    {INTENT_PAYLOAD_TO_TOOLTIP_INFORMATION_FULL_TEXT[SecondaryIntent]}
                                </TooltipInformationalFullTextWrapper>
                            </IntentHeaderWrapper>
                            <DisclaimerTextWrapper>
                                {MAP_INTENT_PAYLOAD_TO_INTENT_TOOLTIP_DISCLAIMER[SecondaryIntent]}
                            </DisclaimerTextWrapper>
                        </IntentWrapper>
                    ) : null}
                </TooltipWrapper>
            )}
            config={{ placement: "right" }}
        >
            {children}
        </PopupHoverContainer>
    );
};

/**
 * @deprecated
 * We are about to remove it soon and add it as a pillCell to pro-logic-components
 */
export const IntentCell = ({
    value: { PrimaryIntent, SecondaryIntent },
    row,
}: {
    value: { PrimaryIntent: Intent; SecondaryIntent: Intent };
    row: any;
}) => {
    if (!PrimaryIntent && !SecondaryIntent) {
        return <IntentHeaderWrapper>{NO_DATA_MESSAGE}</IntentHeaderWrapper>;
    }
    if (!SecondaryIntent) {
        return (
            <IntentCellTooltip
                searchTerm={row.SearchTerm}
                date={row.durationText}
                PrimaryIntent={PrimaryIntent}
                SecondaryIntent={SecondaryIntent}
            >
                <IntentHeaderWrapper>
                    <Label>{MAP_INTENT_PAYLOAD_TO_INTENT_REPRESENTATION[PrimaryIntent]}</Label>
                </IntentHeaderWrapper>
            </IntentCellTooltip>
        );
    }
    return (
        <IntentCellTooltip
            searchTerm={row.SearchTerm}
            date={row.durationText}
            PrimaryIntent={PrimaryIntent}
            SecondaryIntent={SecondaryIntent}
        >
            <IntentHeaderWrapper>
                <Label>{MAP_INTENT_PAYLOAD_TO_INTENT_REPRESENTATION[PrimaryIntent]}</Label>
                <Label>{MAP_INTENT_PAYLOAD_TO_INTENT_REPRESENTATION[SecondaryIntent]}</Label>
            </IntentHeaderWrapper>
        </IntentCellTooltip>
    );
};
